import React, { useState, useEffect, useCallback } from 'react';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiService } from '../services/apiService';

interface LabResult {
  id: number;
  testName: string;
  value: number;
  units: string;
  status: string;
  referenceMin: number | null;
  referenceMax: number | null;
  confidence: number;
}

interface ReportData {
  id: number;
  filename: string;
  testDate: string | null;
  uploadDate: string;
  results: LabResult[];
}

interface ReportDetailsProps {
  reportId: number;
  onBack: () => void;
  onUpdate?: () => void;
}

export const ReportDetails: React.FC<ReportDetailsProps> = ({ reportId, onBack, onUpdate }) => {
  const { user } = useSimpleAuth();
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [saving, setSaving] = useState(false);

  // Load report details
  const loadReport = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getReport(reportId);

      if (response.status === 'success') {
        setReport(response.data);
        // Initialize edited data
        const initialEdits: any = {
          testDate: response.data.testDate,
          results: {}
        };
        response.data.results.forEach((result: any) => {
          initialEdits.results[result.id] = {
            value: result.value,
            units: result.units,
            referenceMin: result.referenceMin,
            referenceMax: result.referenceMax
          };
        });
        setEditedData(initialEdits);
      }
    } catch (error: any) {
      console.error('Error loading report:', error);
      setError(error.message || 'Failed to load report details');
    } finally {
      setLoading(false);
    }
  }, [user, reportId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  // Handle test date change
  const handleDateChange = (date: Date | null) => {
    setEditedData({
      ...editedData,
      testDate: date ? date.toISOString().split('T')[0] : null
    });
  };

  // Handle result value change
  const handleResultChange = (resultId: number, field: string, value: any) => {
    setEditedData({
      ...editedData,
      results: {
        ...editedData.results,
        [resultId]: {
          ...editedData.results[resultId],
          [field]: value
        }
      }
    });
  };

  // Calculate status based on value and reference range
  const calculateStatus = (value: number, min: number | null, max: number | null): string => {
    if (min === null || max === null) return 'UNKNOWN';
    if (value < min) return 'LOW';
    if (value > max) return 'HIGH';
    return 'NORMAL';
  };

  // Save changes
  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // Prepare update data
      const updates: any[] = [];
      
      // Check for date change
      if (editedData.testDate !== report?.testDate) {
        updates.push({
          type: 'report',
          field: 'testDate',
          value: editedData.testDate
        });
      }

      // Check for result changes
      report?.results.forEach(result => {
        const edited = editedData.results[result.id];
        if (edited) {
          // Check value change
          if (edited.value !== result.value) {
            updates.push({
              type: 'result',
              resultId: result.id,
              field: 'value',
              oldValue: result.value,
              newValue: parseFloat(edited.value),
              status: calculateStatus(
                parseFloat(edited.value),
                edited.referenceMin || result.referenceMin,
                edited.referenceMax || result.referenceMax
              )
            });
          }

          // Check units change
          if (edited.units !== result.units) {
            updates.push({
              type: 'result',
              resultId: result.id,
              field: 'units',
              oldValue: result.units,
              newValue: edited.units
            });
          }

          // Check reference range changes
          if (edited.referenceMin !== result.referenceMin || 
              edited.referenceMax !== result.referenceMax) {
            updates.push({
              type: 'result',
              resultId: result.id,
              field: 'referenceRange',
              oldMin: result.referenceMin,
              oldMax: result.referenceMax,
              newMin: edited.referenceMin ? parseFloat(edited.referenceMin) : null,
              newMax: edited.referenceMax ? parseFloat(edited.referenceMax) : null,
              status: calculateStatus(
                edited.value || result.value,
                edited.referenceMin ? parseFloat(edited.referenceMin) : null,
                edited.referenceMax ? parseFloat(edited.referenceMax) : null
              )
            });
          }
        }
      });

      if (updates.length === 0) {
        alert('No changes to save');
        setEditMode(false);
        return;
      }

      // Send updates to backend in the format the backend expects
      const response = await apiService.updateReport(reportId, {
        testDate: editedData.testDate,
        results: editedData.results
      });

      if (response.status === 'success') {
        alert('Changes saved successfully');
        setEditMode(false);
        loadReport(); // Reload to get updated data
        onUpdate?.(); // Notify parent to refresh list
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report details...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error || 'Report not found'}
          </div>
          <button
            onClick={onBack}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            ← Back to reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{report.filename}</h1>
                <p className="text-sm text-gray-500">
                  Uploaded on {new Date(report.uploadDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      loadReport(); // Reset changes
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Edit Report
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Test Date */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Date
              </label>
              {editMode ? (
                <DatePicker
                  selected={editedData.testDate ? new Date(editedData.testDate) : null}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select test date"
                  maxDate={new Date()}
                />
              ) : (
                <p className="text-gray-900">
                  {report.testDate 
                    ? new Date(report.testDate).toLocaleDateString()
                    : 'Not specified'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Results
              </label>
              <p className="text-gray-900">{report.results.length}</p>
            </div>
          </div>
        </div>

        {/* Lab Results */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Lab Results</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Units
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lab Reference Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.results.map((result) => {
                  const edited = editedData.results[result.id] || {};
                  const currentValue = edited.value ?? result.value;
                  const currentMin = edited.referenceMin ?? result.referenceMin;
                  const currentMax = edited.referenceMax ?? result.referenceMax;
                  const currentStatus = calculateStatus(
                    parseFloat(currentValue),
                    currentMin ? parseFloat(currentMin) : null,
                    currentMax ? parseFloat(currentMax) : null
                  );

                  return (
                    <tr key={result.id} className={editMode ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {result.testName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {editMode ? (
                          <input
                            type="number"
                            value={edited.value ?? result.value}
                            onChange={(e) => handleResultChange(result.id, 'value', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            step="0.01"
                          />
                        ) : (
                          result.value
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {editMode ? (
                          <input
                            type="text"
                            value={edited.units ?? result.units}
                            onChange={(e) => handleResultChange(result.id, 'units', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          result.units
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {editMode ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={edited.referenceMin ?? result.referenceMin ?? ''}
                              onChange={(e) => handleResultChange(result.id, 'referenceMin', e.target.value)}
                              placeholder="Min"
                              className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              step="0.01"
                            />
                            <span>-</span>
                            <input
                              type="number"
                              value={edited.referenceMax ?? result.referenceMax ?? ''}
                              onChange={(e) => handleResultChange(result.id, 'referenceMax', e.target.value)}
                              placeholder="Max"
                              className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              step="0.01"
                            />
                          </div>
                        ) : (
                          result.referenceMin !== null && result.referenceMax !== null
                            ? `${result.referenceMin} - ${result.referenceMax}`
                            : 'N/A'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          currentStatus === 'HIGH' 
                            ? 'bg-red-100 text-red-800'
                            : currentStatus === 'LOW'
                            ? 'bg-blue-100 text-blue-800'
                            : currentStatus === 'NORMAL'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {currentStatus}
                          {editMode && currentStatus !== result.status && (
                            <span className="ml-1 text-xs">
                              (was {result.status})
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Mode Notice */}
        {editMode && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-800">Edit Mode Active</h3>
                <p className="mt-1 text-sm text-blue-700">
                  You can edit values, units, and reference ranges. Status will be automatically recalculated based on your changes.
                  Changes are not saved until you click "Save Changes".
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDetails;